import { FormPreviewDialog } from '@/components/FormPreview/FormPreviewDialog'
import { Button } from '@/components/ui/button'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { Switch } from '@/components/ui/switch'
import type { FormDetailContract } from '@/contracts/questionnaire'
import { saveFormAsTemplate } from '@/lib/data-client/form-templates'
import {
  builderPaneDefaults,
  clampPaneWidth,
  getThreePaneGridTemplate,
} from '@/lib/resizable-panes'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { cn, estimateMinutes } from '@/lib/utils'
import {
  type BuilderSnapshot,
  createBuilderDraftPreviewSnapshot,
  useFormBuilderStore,
} from '@/stores/formBuilderStore'
import type { PublicForm } from '@/types/form'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, CopyPlus, Eye, Save, Undo2, X } from 'lucide-react'
import { useEffect, useMemo, useState, type CSSProperties, type PointerEvent } from 'react'
import { toast } from 'sonner'
import { QuestionEditor } from './QuestionEditor'
import { QuestionList } from './QuestionList'
import { SettingsPanel } from './SettingsPanel'

interface FormBuilderClientProps {
  initialForm: PublicForm | FormDetailContract
  mode?: 'route' | 'dialog'
  variant?: 'form' | 'template'
  onClose?: () => void
  onSaveBuilderSnapshot?: (snapshot: BuilderSnapshot) => Promise<void>
  backTo?: string
}

export function FormBuilderClient({
  initialForm,
  mode = 'route',
  variant = 'form',
  onClose,
  onSaveBuilderSnapshot,
  backTo = '/dashboard/forms',
}: FormBuilderClientProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const featureVisibility = useSystemFeatureVisibility()
  const form = useFormBuilderStore((state) => state.form)
  const questions = useFormBuilderStore((state) => state.questions)
  const logicJumps = useFormBuilderStore((state) => state.logicJumps)
  const questionGroups = useFormBuilderStore((state) => state.questionGroups)
  const questionOutline = useFormBuilderStore((state) => state.questionOutline)
  const isDirty = useFormBuilderStore((state) => state.isDirty)
  const load = useFormBuilderStore((state) => state.load)
  const updateForm = useFormBuilderStore((state) => state.updateForm)
  const save = useFormBuilderStore((state) => state.save)
  const markSaved = useFormBuilderStore((state) => state.markSaved)
  const undo = useFormBuilderStore((state) => state.undo)
  const redo = useFormBuilderStore((state) => state.redo)
  const selectQuestion = useFormBuilderStore((state) => state.selectQuestion)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [leftPaneWidth, setLeftPaneWidth] = useState(320)
  const [rightPaneWidth, setRightPaneWidth] = useState(340)
  const [leftPaneCollapsed, setLeftPaneCollapsed] = useState(false)
  const [rightPaneCollapsed, setRightPaneCollapsed] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)

  const previewSnapshot = useMemo(
    () =>
      createBuilderDraftPreviewSnapshot({
        form,
        questions,
        logicJumps,
        questionGroups,
        questionOutline,
      }),
    [form, logicJumps, questionGroups, questionOutline, questions],
  )

  useEffect(() => {
    load(
      initialForm,
      initialForm.questions,
      initialForm.logicJumps,
      'groups' in initialForm ? initialForm.groups : [],
      'outline' in initialForm ? initialForm.outline : [],
    )
  }, [initialForm, load])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        void saveCurrentSnapshot().then(() => toast.success('已儲存'))
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') undo()
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') redo()
      if (event.key === 'Escape') selectQuestion(null)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [redo, saveCurrentSnapshot, selectQuestion, undo])

  useEffect(() => {
    if (!isDirty) return
    const timer = window.setTimeout(() => {
      void saveCurrentSnapshot()
    }, 2000)
    return () => window.clearTimeout(timer)
  }, [isDirty, saveCurrentSnapshot])

  useEffect(() => {
    function beforeUnload(event: BeforeUnloadEvent) {
      if (!isDirty) return
      event.preventDefault()
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [isDirty])

  function handleBack() {
    if (onClose) {
      onClose()
      return
    }
    void navigate({ to: backTo })
  }

  function openDraftPreview() {
    setPreviewOpen(true)
  }

  const fillMode = form.settings.fillMode === 'step' ? 'step' : 'one_page'
  function setFillMode(nextMode: 'one_page' | 'step') {
    updateForm({ settings: { ...form.settings, fillMode: nextMode } })
  }

  function setPublicationStatus(published: boolean) {
    const status = published ? 'published' : 'draft'
    updateForm({ status })
    toast.success(published ? '問卷狀態已設為公開' : '問卷狀態已設為草稿')
  }

  function getCurrentBuilderSnapshot(): BuilderSnapshot {
    return {
      form,
      questions,
      logicJumps,
      questionGroups,
      questionOutline,
    }
  }

  async function saveCurrentSnapshot() {
    if (onSaveBuilderSnapshot) {
      await onSaveBuilderSnapshot(getCurrentBuilderSnapshot())
      markSaved()
      return
    }
    await save()
  }

  async function saveCurrentFormAsTemplate() {
    if (savingTemplate) return
    setSavingTemplate(true)
    try {
      await saveCurrentSnapshot()
      await saveFormAsTemplate(form.id, {
        title: `${form.title} 範本`,
        description: form.description,
        category: 'general',
      })
      await queryClient.invalidateQueries({ queryKey: ['form-templates'] })
      toast.success('已存成問卷範本')
    } finally {
      setSavingTemplate(false)
    }
  }

  function startPaneResize(pane: 'left' | 'right', event: PointerEvent<HTMLButtonElement>) {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = pane === 'left' ? leftPaneWidth : rightPaneWidth
    const config = pane === 'left' ? builderPaneDefaults.left : builderPaneDefaults.right
    const setWidth = pane === 'left' ? setLeftPaneWidth : setRightPaneWidth
    const setCollapsed = pane === 'left' ? setLeftPaneCollapsed : setRightPaneCollapsed

    function onPointerMove(moveEvent: globalThis.PointerEvent) {
      const delta = moveEvent.clientX - startX
      const nextWidth = pane === 'left' ? startWidth + delta : startWidth - delta
      setCollapsed(false)
      setWidth(clampPaneWidth(nextWidth, config))
    }

    function onPointerUp() {
      window.removeEventListener('pointermove', onPointerMove)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp, { once: true })
  }

  const builderGridColumns = getThreePaneGridTemplate({
    leftWidth: leftPaneWidth,
    rightWidth: rightPaneWidth,
    leftCollapsed: leftPaneCollapsed,
    rightCollapsed: rightPaneCollapsed,
    centerMinWidth: 520,
  })

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col bg-slate-50',
        mode === 'dialog' ? 'h-full' : 'h-[calc(100vh-4rem)]',
      )}
    >
      <header className="flex shrink-0 flex-col gap-2 border-b border-slate-200 bg-white px-3 py-2 shadow-sm md:h-16 md:flex-row md:items-center md:justify-between md:px-4 md:py-0">
        <div className="flex w-full min-w-0 items-center gap-2 md:w-auto md:flex-1 md:gap-3">
          {mode === 'dialog' ? null : (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              aria-label="返回問卷清單"
              title="返回問卷清單"
            >
              <ArrowLeft className="size-4" />
            </Button>
          )}
          <input
            value={form.title}
            onChange={(event) => updateForm({ title: event.target.value })}
            className="min-w-0 flex-1 bg-transparent text-base font-semibold outline-none md:text-lg"
          />
        </div>
        <div className="hidden text-sm text-slate-500 md:block">
          {questions.length} 個問題 · 約 {estimateMinutes(questions.length)} 分鐘
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 md:w-auto md:justify-end">
          <div
            className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1"
            aria-label="問卷填寫模式"
          >
            <Button
              type="button"
              size="sm"
              variant={fillMode === 'one_page' ? 'default' : 'ghost'}
              onClick={() => setFillMode('one_page')}
            >
              一頁式
            </Button>
            <Button
              type="button"
              size="sm"
              variant={fillMode === 'step' ? 'default' : 'ghost'}
              onClick={() => setFillMode('step')}
            >
              分題式
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={openDraftPreview}>
            <Eye className="size-4" />
            <span className="hidden sm:inline">預覽</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void saveCurrentSnapshot().then(() => toast.success('已儲存'))}
          >
            {isDirty ? (
              <Save className="size-4" />
            ) : (
              <CheckCircle2 className="size-4 text-emerald-600" />
            )}
            <span className="hidden sm:inline">{isDirty ? '儲存' : '已同步'}</span>
          </Button>
          {variant === 'form' && featureVisibility['forms.templates'] ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void saveCurrentFormAsTemplate()}
              disabled={savingTemplate}
            >
              <CopyPlus className="size-4" />
              <span className="hidden sm:inline">{savingTemplate ? '儲存中' : '存成範本'}</span>
            </Button>
          ) : null}
          <Button variant="ghost" size="icon" onClick={undo}>
            <Undo2 className="size-4" />
          </Button>
          {variant === 'form' ? (
            <div className="flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <span className="hidden text-slate-600 sm:inline">草稿</span>
              <Switch
                aria-label="公開狀態"
                checked={form.status === 'published'}
                disabled={form.status === 'archived'}
                onCheckedChange={setPublicationStatus}
              />
              <span className="font-medium text-slate-800">公開</span>
            </div>
          ) : (
            <div className="flex h-9 items-center rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm font-medium text-emerald-800">
              範本編輯
            </div>
          )}
          {mode === 'dialog' ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              aria-label="關閉問卷編輯"
              title="關閉問卷編輯"
              className="ml-auto"
            >
              <X className="size-4" />
            </Button>
          ) : null}
        </div>
      </header>
      <div
        data-testid="form-builder-grid"
        className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--form-builder-grid-columns)]"
        style={{ '--form-builder-grid-columns': builderGridColumns } as CSSProperties}
      >
        <div className="relative min-h-0 min-w-0 overflow-hidden">
          <QuestionList />
          <PaneResizeHandle
            edge="right"
            collapsed={leftPaneCollapsed}
            resizeLabel="拖曳調整左欄寬度"
            collapseLabel="收合左欄"
            expandLabel="展開左欄"
            onResizeStart={(event) => startPaneResize('left', event)}
            onToggle={() => setLeftPaneCollapsed((collapsed) => !collapsed)}
          />
        </div>
        <main className="min-h-0 min-w-0 overflow-hidden">
          <QuestionEditor onOpenFullPreview={openDraftPreview} />
        </main>
        <div className="relative min-h-0 min-w-0 overflow-hidden">
          <SettingsPanel />
          <PaneResizeHandle
            edge="left"
            collapsed={rightPaneCollapsed}
            resizeLabel="拖曳調整右欄寬度"
            collapseLabel="收合右欄"
            expandLabel="展開右欄"
            onResizeStart={(event) => startPaneResize('right', event)}
            onToggle={() => setRightPaneCollapsed((collapsed) => !collapsed)}
          />
        </div>
      </div>
      {previewOpen ? (
        <FormPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          snapshot={previewSnapshot}
        />
      ) : null}
    </div>
  )
}
